import type { KioskSubmission } from '@ecc/shared';
import { ApiError, NetworkError } from '../api/client';
import { listPending, markFailed, markSynced, markSyncing, purgeSynced } from './queue';

/**
 * Motore di sincronizzazione (§9.3).
 *
 * Invia i record in coda e interpreta gli esiti. `DUPLICATE` è un successo: il
 * server ha già accettato quel `submissionId`, quindi il record locale va
 * chiuso e il payload eliminato, non ritentato all'infinito.
 */

type SyncOutcome = {
  submissionId: string;
  status: 'ACCEPTED' | 'DUPLICATE' | 'REJECTED';
  message?: string;
};

export type SyncReport = {
  readonly attempted: number;
  readonly synced: number;
  readonly rejected: number;
  readonly failed: number;
  readonly offline: boolean;
};

const BATCH_SIZE = 25;

async function postBatch(submissions: KioskSubmission[]): Promise<SyncOutcome[]> {
  const response = await fetch('/api/kiosk/sync', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ submissions }),
  });

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => ({}));
    const code = (body as { code?: string }).code ?? 'SYNC_FAILED';
    throw new ApiError(response.status, code, 'Sincronizzazione non riuscita');
  }

  const body = (await response.json()) as { results: SyncOutcome[] };
  return body.results;
}

/**
 * Tenta di sincronizzare la coda.
 *
 * Non solleva in caso di rete assente: è una condizione prevista in fiera, non
 * un errore da propagare all'interfaccia del visitatore.
 */
export async function syncPending(): Promise<SyncReport> {
  const pending = await listPending();
  const ready = pending.filter((record) => record.payload !== null).slice(0, BATCH_SIZE);

  if (ready.length === 0) {
    return { attempted: 0, synced: 0, rejected: 0, failed: 0, offline: false };
  }

  await Promise.all(ready.map((record) => markSyncing(record.localId)));

  let results: SyncOutcome[];
  try {
    results = await postBatch(ready.map((record) => record.payload as KioskSubmission));
  } catch (error) {
    const offline = error instanceof NetworkError || !navigator.onLine;
    const message = error instanceof Error ? error.message : 'Errore sconosciuto';
    await Promise.all(ready.map((record) => markFailed(record.localId, message)));
    return { attempted: ready.length, synced: 0, rejected: 0, failed: ready.length, offline };
  }

  let synced = 0;
  let rejected = 0;

  for (const result of results) {
    if (result.status === 'ACCEPTED' || result.status === 'DUPLICATE') {
      // In entrambi i casi il server ha il record: il payload locale non serve più.
      await markSynced(result.submissionId);
      synced += 1;
    } else {
      // Rifiutato definitivamente: ritentare non cambierebbe l'esito. Resta in
      // coda come FAILED perché un operatore possa accorgersene.
      await markFailed(result.submissionId, result.message ?? 'Rifiutato dal server');
      rejected += 1;
    }
  }

  await purgeSynced();

  return { attempted: ready.length, synced, rejected, failed: 0, offline: false };
}

/**
 * Avvia la sincronizzazione periodica e al ritorno della connessione.
 * Restituisce la funzione per fermarla.
 */
export function startAutoSync(intervalMs = 30_000, onReport?: (report: SyncReport) => void): () => void {
  let stopped = false;

  const run = async (): Promise<void> => {
    if (stopped) return;
    try {
      const report = await syncPending();
      if (report.attempted > 0) onReport?.(report);
    } catch {
      // La sincronizzazione è opportunistica: un errore qui non deve mai
      // interrompere la raccolta in corso allo stand.
    }
  };

  const timer = setInterval(() => void run(), intervalMs);
  const onOnline = () => void run();
  window.addEventListener('online', onOnline);

  void run();

  return () => {
    stopped = true;
    clearInterval(timer);
    window.removeEventListener('online', onOnline);
  };
}
